import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";

interface CountdownProps {
  initialSeconds?: number;
  leadingZero?: boolean;
  timeout?: () => void;
  stopTimestamp?: number;
}

export interface CountdownRef {
  reset: () => void;
}

const Countdown = forwardRef<CountdownRef, CountdownProps>(
  ({ initialSeconds, leadingZero = false, timeout, stopTimestamp }, ref) => {
    const [secondsLeft, setSecondsLeft] = useState(
      Math.max(
        0,
        stopTimestamp
          ? Math.floor((stopTimestamp - +new Date()) / 1000)
          : initialSeconds || 0
      )
    );
    const [resetTrigger, setResetTrigger] = useState(0);

    useImperativeHandle(ref, () => ({
      reset: () => {
        setResetTrigger((prev) => prev + 1);
      },
    }));

    useEffect(() => {
      setSecondsLeft(
        Math.max(
          0,
          stopTimestamp
            ? Math.floor((stopTimestamp - +new Date()) / 1000)
            : initialSeconds || 0
        )
      );
    }, [initialSeconds, stopTimestamp, resetTrigger]);

    useEffect(() => {
      if (secondsLeft <= 0) return;
      if (secondsLeft <= 1) {
        timeout?.();
      }
      const timer = setInterval(() => {
        setSecondsLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
      // eslint-disable-next-line react-hooks/exhaustive-deps -- re-runs every tick via secondsLeft, re-capturing timeout each second; never stale
    }, [secondsLeft]);

    const formatTime = (totalSeconds: number) => {
      if (totalSeconds <= 0) {
        return leadingZero ? "00s" : "0s";
      }

      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      const formatPart = (value: number, unit: string) => {
        if (leadingZero && value < 10) {
          return `0${value}${unit}`;
        }
        return `${value}${unit}`;
      };

      const parts = [];
      parts.push(formatPart(seconds, "s"));
      if (hours > 0 || minutes > 0) {
        parts.push(formatPart(minutes, "m"));
      }
      if (hours > 0) {
        parts.push(formatPart(hours, "h"));
      }

      return parts.reverse().join(" ");
    };

    return <>{formatTime(secondsLeft)}</>;
  }
);

export default Countdown;
